import { useCallback, useMemo } from 'react';
import { membershipsSelector } from 'slices/memberships.slice';
import { selectObjectTypesMap } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import { getMemberTypes } from 'utils/associations';
import type { TTEObject } from '@timeedit/types';
import { Association } from './Association';

type MembershipProps = {
  object: TTEObject;
  onChange: (object: Partial<TTEObject>) => void;
  /** IDs of newly added associations that should be highlighted */
  changedIds?: string[];
};

export const Membership = ({
  object,
  onChange,
  changedIds,
}: MembershipProps) => {
  const { members: membersOfObject } = object;

  const memberships = useAppSelector(membershipsSelector);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);

  const memberTypes = useMemo(
    () => getMemberTypes(object.types, memberships, objectTypesMap),
    [memberships, object, objectTypesMap]
  );

  const onSelectedObjectsChange = useCallback(
    (objects: TTEObject[]) => {
      const newMembers = objects.map((object) => ({
        objectId: object.id,
        start: 0,
        end: 0,
      }));

      onChange({ members: newMembers });
    },
    [membersOfObject, onChange]
  );

  const onRemove = useCallback(
    (objects: TTEObject[]) => {
      const newMembers = membersOfObject.filter((member) =>
        objects.every((object) => object.id !== member.objectId)
      );
      onChange({ members: newMembers });
    },
    [membersOfObject, onChange]
  );

  const memberObjectIds = useMemo(
    () => membersOfObject.map((member) => member.objectId),
    [membersOfObject]
  );

  return (
    <Association
      tableId="objects.membership"
      objectIds={memberObjectIds}
      objectTypes={memberTypes}
      onChange={onSelectedObjectsChange}
      onRemove={onRemove}
      changedIds={changedIds}
    />
  );
};
